import { useSyncExternalStore } from 'react';

import type { DispatchBoardDay } from '../model';
import {
  type FieldRole,
  getActiveRole,
  setActiveRole,
  subscribeRole,
} from './field-role-store';

/**
 * Which personas a user is genuinely entitled to for a given day:
 *   - `worker`     — they're a member of any crew today.
 *   - `supervisor` — they're the lead of any crew today.
 *   - `compliance` — they're the inspector on any compliance activity today.
 *
 * Used to limit the role switcher to lenses that actually have data. In the
 * prototype the switcher still lets you preview every persona; once the
 * backend lands this becomes the authoritative gate.
 */
export function deriveAvailableRoles(
  day: DispatchBoardDay | undefined,
  userId: string,
): FieldRole[] {
  if (!day) return ['worker'];
  const roles = new Set<FieldRole>();

  for (const crew of day.crews) {
    if (crew.leadUserId === userId) roles.add('supervisor');
    if (crew.members.some((m) => m.userId === userId)) roles.add('worker');
  }

  const allActivities = [
    ...day.standaloneActivities,
    ...day.crews.flatMap((c) => c.jobs),
  ];
  const isInspector = allActivities.some(
    (a) => a.isCompliance && a.compliance?.inspectorId === userId,
  );
  if (isInspector) roles.add('compliance');

  if (roles.size === 0) roles.add('worker');
  return ['worker', 'supervisor', 'compliance'].filter((r) =>
    roles.has(r as FieldRole),
  ) as FieldRole[];
}

/**
 * The active field persona. Backed by a localStorage-persisted store so the
 * choice survives reloads and the in-app switcher can flip personas without a
 * backend. Returns the role plus a setter.
 */
export function useFieldRole(): {
  role: FieldRole;
  setRole: (role: FieldRole) => void;
} {
  const role = useSyncExternalStore(
    subscribeRole,
    getActiveRole,
    () => getActiveRole(),
  );
  return { role, setRole: setActiveRole };
}
